import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  Download,
  RefreshCw,
  FileCheck,
  Wrench,
  ClipboardCheck,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { ServiceBudgetCard } from '../components/reports/ServiceBudgetCard';

const STATUS_CONFIG = {
  green:  { label: 'Compliant',     bg: 'bg-green-100',  text: 'text-green-700',  border: 'border-green-200',  icon: CheckCircle2,  dot: 'bg-green-500'  },
  amber:  { label: 'Warning',       bg: 'bg-amber-100',  text: 'text-amber-700',  border: 'border-amber-200',  icon: AlertTriangle, dot: 'bg-amber-400'  },
  red:    { label: 'Non-Compliant', bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-200',    icon: XCircle,       dot: 'bg-red-500'    },
};

const ITEM_ICONS = {
  license_disc:       FileText,
  registration_disc:  FileCheck,
  service:            Wrench,
  inspection:         ClipboardCheck,
};

const ITEM_LABELS = {
  license_disc:       'License Disc',
  registration_disc:  'Registration Disc',
  service:            'Service Due',
  inspection:         'Last Inspection',
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.amber;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-3.5 h-3.5" />
      {cfg.label}
    </span>
  );
};

const StatusDot = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.amber;
  return <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${cfg.dot}`} />;
};

export default function CompliancePage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // vehicle_id

  const fetchCompliance = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getFleetCompliance();
      setData(result);
    } catch {
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompliance(); }, [fetchCompliance]);

  const handleDownloadPDF = async (vehicleId, vehicleName) => {
    setDownloading(vehicleId);
    try {
      const blob = await api.downloadCompliancePDF(vehicleId);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `compliance_${vehicleName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const summary = data?.summary || { total: 0, red: 0, amber: 0, green: 0 };
  const vehicles = useMemo(() => data?.vehicles || [], [data]);
  const nonCompliant = useMemo(() => vehicles.filter(v => v.overall_status === 'red'), [vehicles]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Loading compliance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Fleet Compliance</h1>
          <p className="text-sm text-gray-500 mt-1">License discs, registration, service intervals &amp; inspections</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchCompliance} data-testid="refresh-compliance-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Vehicles', value: summary.total, colorClass: 'text-gray-900', bg: 'bg-gray-50' },
          { label: 'Compliant',      value: summary.green, colorClass: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Warning',        value: summary.amber, colorClass: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Non-Compliant',  value: summary.red,   colorClass: 'text-red-600',   bg: 'bg-red-50' },
        ].map(card => (
          <Card key={card.label} className={`border-gray-200 ${card.bg}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">{card.label}</p>
              <p className={`text-3xl font-heading font-bold ${card.colorClass}`} data-testid={`summary-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>{card.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Vehicle Compliance Table */}
      {vehicles.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-gray-400">
            <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No vehicles found</p>
            <p className="text-sm mt-1">Add vehicles to track compliance</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">Vehicle Compliance Overview</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-5 py-3 text-left font-semibold text-gray-600">Vehicle</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">License Disc</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Reg Disc</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Service</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Inspection</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Overall</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {vehicles.map(v => (
                    <tr key={v.vehicle_id} className="hover:bg-gray-50 transition-colors" data-testid={`compliance-row-${v.vehicle_id}`}>
                      <td className="px-5 py-4">
                        <Link to={`/vehicles/${v.vehicle_id}`} className="flex items-center gap-2 hover:underline group">
                          <span className="font-medium text-gray-900 group-hover:text-blue-600">{v.vehicle_name}</span>
                          <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500" />
                        </Link>
                        <p className="text-xs text-gray-400 mt-0.5">{v.registration}</p>
                      </td>
                      {['license_disc', 'registration_disc', 'service', 'inspection'].map(key => {
                        const item = v.items?.[key];
                        const Icon = ITEM_ICONS[key];
                        return (
                          <td key={key} className="px-4 py-4 text-center" data-testid={`${key}-status-${v.vehicle_id}`}>
                            <div className="flex flex-col items-center gap-1">
                              <StatusDot status={item?.status || 'amber'} />
                              <span className={`text-xs font-medium ${
                                item?.status === 'red' ? 'text-red-600' :
                                item?.status === 'amber' ? 'text-amber-600' : 'text-green-600'
                              }`}>
                                {item?.status === 'red' ? 'Non-Compliant' :
                                 item?.status === 'amber' ? 'Warning' : 'OK'}
                              </span>
                              {item?.message && (
                                <span className="text-xs text-gray-400 max-w-[100px] text-center leading-tight">{item.message}</span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-4 py-4 text-center">
                        <StatusBadge status={v.overall_status} />
                      </td>
                      <td className="px-4 py-4 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPDF(v.vehicle_id, v.vehicle_name)}
                          disabled={downloading === v.vehicle_id}
                          data-testid={`download-pdf-${v.vehicle_id}`}
                          className="text-xs"
                        >
                          {downloading === v.vehicle_id ? (
                            <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5 mr-1" />
                          )}
                          PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Non-compliant details */}
      {nonCompliant.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading text-red-800 flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              Non-Compliant Vehicles — Action Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {nonCompliant.map(v =>
                v.issues?.map((issue, i) => (
                  <li key={`${v.vehicle_id}-${i}`} className="flex items-start gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 shrink-0" />
                    <span>
                      <strong className="text-red-900">{v.vehicle_name}</strong>
                      <span className="text-red-700"> — {issue.item}: {issue.message}</span>
                    </span>
                  </li>
                ))
              )}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Upcoming service-cost forecast (editable) */}
      <ServiceBudgetCard months={3} />
    </div>
  );
}
